// Copyright (c) 2026 Isolated Networks. All rights reserved.
// Proprietary and confidential. Unauthorized copying, distribution, or use
// of this file, via any medium, is strictly prohibited.
// SPDX-License-Identifier: LicenseRef-Proprietary

/**
 * Display rules for a firmware push failure.
 *
 * The device API often returns only RAUC's last summary line, so a failed
 * install that already activated the other slot cannot be told apart from any
 * other install failure. `revert_required` is the case where the payload (or
 * the backend, once it saw that payload) proved the slot was activated.
 * Everything else that failed an install needs an explicit reboot confirmation.
 */

export const REVERT_HEADLINE = 'Do not reboot: revert required';

export const CONFIRM_NOTE =
  'Reboot needs confirmation. A failed install may have activated the new rootfs while the boot partition still has the previous kernel.';

export const REBOOT_CONFIRM_TITLE = 'Reboot after a failed install';

export const REBOOT_CONFIRM_BODY =
  'A failed install can leave the bootloader pointed at the new rootfs while the boot partition still has the previous kernel. Rebooting may boot a mismatched pair. Reboot only after you have checked that the booted slot and the activated slot match.';

export const PREVIEW_CHARS = 180;

export const REBOOT_REVERT = 'revert_required';
export const REBOOT_CONFIRM = 'confirm_required';

export function rebootSafetyOf(ds) {
  if (!ds) return null;
  if (ds.reboot_safety === REBOOT_REVERT || ds.reboot_safety === REBOOT_CONFIRM) {
    return ds.reboot_safety;
  }
  const message = typeof ds.message === 'string' ? ds.message : '';
  if (message.includes(REVERT_HEADLINE)) return REBOOT_REVERT;
  return null;
}

export function showsFailureText(status, safety) {
  return (
    status === 'failed' ||
    status === 'error' ||
    status === 'timeout' ||
    status === 'interrupted' ||
    safety === REBOOT_REVERT ||
    safety === REBOOT_CONFIRM
  );
}

export function failureNeedsExpand(message) {
  return typeof message === 'string' && message.length > PREVIEW_CHARS;
}

export function failurePreview(message, expanded) {
  const text = typeof message === 'string' ? message : '';
  if (expanded || text.length <= PREVIEW_CHARS) return text;
  return `${text.slice(0, PREVIEW_CHARS).trimEnd()}…`;
}

/**
 * @returns {{ visible: boolean, disabled: boolean, confirm: boolean }}
 * `disabled` is the hard stop. `confirm` still allows the reboot after the
 * operator accepts the risk. A successful `rauc_complete` row is unchanged.
 */
export function rebootActionFor(ds) {
  if (!ds) return { visible: false, disabled: true, confirm: false };
  const safety = rebootSafetyOf(ds);
  const status = ds.status || '';
  if (safety === REBOOT_REVERT) {
    return { visible: true, disabled: true, confirm: false };
  }
  if (safety === REBOOT_CONFIRM || status === 'failed' || status === 'error') {
    return { visible: true, disabled: false, confirm: true };
  }
  if (status === 'rauc_complete' && !ds.auto_reboot) {
    return { visible: true, disabled: false, confirm: false };
  }
  return { visible: false, disabled: true, confirm: false };
}

export const DEFAULT_BLOCKED_FIRMWARE_VERSION = '1.2.193';

export function firmwareBlockMessage(version) {
  const token = version || DEFAULT_BLOCKED_FIRMWARE_VERSION;
  if (token === DEFAULT_BLOCKED_FIRMWARE_VERSION) {
    return `Firmware bundle is blocked and will not be sent to a device: filename or version matches ${token}. Stargate ${token} was HAB-signed with the wrong keys and must not be pushed.`;
  }
  return `Firmware bundle is blocked and will not be sent to a device: filename or version matches ${token}. This bundle is on the firmware block list and must not be pushed.`;
}

/** Client-side stop for the default blocked build. The server list is authoritative. */
export function firmwareBundleBlockReason(filename) {
  const name = typeof filename === 'string' ? filename : '';
  if (name.includes(DEFAULT_BLOCKED_FIRMWARE_VERSION)) {
    return firmwareBlockMessage(DEFAULT_BLOCKED_FIRMWARE_VERSION);
  }
  return null;
}

export async function copyText(text) {
  try {
    if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (_) {
    return false;
  }
  return false;
}
